import type { GetMessagesQuery } from '#src/graphql/generated';

import { useApolloClient } from '@apollo/client/react';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  hasComposerContent,
  useDocumentChatComposer,
} from './document-chat-composer-context';
import { DocumentComposerModelPicker } from './document-composer-model-picker';
import { useDocumentContext } from './document-context';
import { DocumentThreadPicker } from './document-thread-picker';
import { useEditDocument } from './edit-document-context';
import { stringifyEditorJs } from './editorjs-types';
import { usePromptSubmit } from './prompt-command';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { MessageList } from '#src/components/message-list';
import { PageContentSidebarPortal } from '#src/components/page-content-sidebar-portal';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { useDocumentScopedMessages } from '#src/hooks/use-document-scoped-messages';
import { useScrollMessageListToEnd } from '#src/hooks/use-scroll-message-list-to-end';
import { usePageContentSidebar } from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';

export interface DocumentChatFooterProps {
  /** When false, sidebar chat UI is hidden (e.g. narrow layout); the page content sidebar column is hidden too. */
  active?: boolean;
}

/**
 * Document AI chat: when a thread is focused, show that thread’s messages above the composer;
 * otherwise only the thread picker lists threads (full history in the messages drawer). Submit
 * runs the document AI edit processor. Portaled into {@link PageContentSidebar}.
 */
export function DocumentChatFooter({ active = true }: DocumentChatFooterProps) {
  const { documentId, isNewDocument } = useDocumentContext();
  const apolloClient = useApolloClient();
  const user = useCurrentUser();
  const { setContentSidebarSlotActive, setContentSidebarDismissed } =
    usePageContentSidebar();
  const prevActiveRef = useRef<boolean | undefined>(undefined);

  useLayoutEffect(() => {
    setContentSidebarSlotActive(active);
    return () => {
      setContentSidebarSlotActive(false);
      setContentSidebarDismissed(false);
    };
  }, [active, setContentSidebarSlotActive, setContentSidebarDismissed]);

  useEffect(() => {
    if (active && prevActiveRef.current === false) {
      setContentSidebarDismissed(false);
    }
    prevActiveRef.current = active;
  }, [active, setContentSidebarDismissed]);
  const urlUnfocusedForLastDocSubmitRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    sortedMessages,
    displayMessages,
    focusedRootId,
    isMessagesInitialLoad,
    messagesError,
    refetchMessages,
    handleRunChatMessageQuery,
    chatMessageQueryLoadingId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
    messagesFeatureActive,
  } = useDocumentScopedMessages(documentId, {
    enabled: active && Boolean(documentId),
  });

  /** Continue the focused thread, or else the newest document thread so each edit appends to the log. */
  const messageLogThreadRootId = useMemo(() => {
    if (focusedRootId) return focusedRootId;
    if (sortedMessages.length === 0) return undefined;
    return sortedMessages[sortedMessages.length - 1]!.id;
  }, [focusedRootId, sortedMessages]);

  const getMessagesVariables = useMemo(
    () => ({
      input: {
        contextFilters: [{ name: 'documentId', value: documentId ?? '' }],
      },
    }),
    [documentId],
  );

  const onEditProcessorQueued = useCallback(
    (sentThreadRootId: string | undefined) => {
      if (!urlUnfocusedForLastDocSubmitRef.current) return;
      if (sentThreadRootId) {
        expandThread(sentThreadRootId);
      }
    },
    [expandThread],
  );

  const onDeferredEditSuccess = useCallback(
    (sentThreadRootId: string | undefined) => {
      if (!urlUnfocusedForLastDocSubmitRef.current) return;
      urlUnfocusedForLastDocSubmitRef.current = false;
      if (sentThreadRootId) {
        return;
      }
      if (!documentId) return;
      const res = apolloClient.readQuery<GetMessagesQuery>({
        query: GET_MESSAGES,
        variables: getMessagesVariables,
      });
      const roots = [...(res?.messages ?? [])].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      const newest = roots[roots.length - 1]?.id;
      if (newest) {
        expandThread(newest);
      }
    },
    [apolloClient, documentId, expandThread, getMessagesVariables],
  );

  useScrollMessageListToEnd(
    messagesEndRef,
    focusedRootId ? displayMessages : [],
  );

  const { setValue, getValue } = useEditDocument();
  const {
    promptText,
    setPromptText,
    clear,
    composerRootRef,
    sessionComposerModel,
    setSessionComposerModel,
    composerModelOptions,
  } = useDocumentChatComposer();
  const {
    handleSubmit,
    disabled: aiSubmitDisabled,
    processingPrompt,
  } = usePromptSubmit({
    documentId,
    isNewDocument,
    focusBlockId: undefined,
    onValueUpdate: setValue,
    getEditorSnapshot: () => stringifyEditorJs(getValue()),
    model: sessionComposerModel,
    threadRootId: messageLogThreadRootId,
    refetchMessages,
    onEditProcessorQueued,
    onDeferredEditSuccess,
  });

  const onSubmitAiEdit = useCallback(async () => {
    if (!hasComposerContent(promptText)) return;
    const wire = promptText.trim();
    if (!wire) return;
    urlUnfocusedForLastDocSubmitRef.current = !focusedRootId;
    const ok = await handleSubmit(wire);
    if (ok) {
      clear();
    } else {
      urlUnfocusedForLastDocSubmitRef.current = false;
    }
  }, [promptText, clear, handleSubmit, focusedRootId]);

  const threadTrailing = messagesFeatureActive ? (
    <DocumentThreadPicker
      roots={sortedMessages}
      focusedRootId={focusedRootId}
      onSelectRoot={expandThread}
      onClearThreadFocus={replaceStripFocusParam}
      disabled={processingPrompt}
    />
  ) : null;

  const placeholder = messagesFeatureActive
    ? focusedRootId
      ? 'Describe how to change the document…'
      : 'e.g., Summarize this section, tighten the intro, fix tone…'
    : isNewDocument
      ? 'Save the document first to use AI modifications'
      : 'Describe the change…';

  const footerContent = (
    <VStack gap={2} align="stretch" width="100%">
      {!documentId && (
        <Text fontSize="xs" color="fg.muted">
          Save the document to use document messages.
        </Text>
      )}
      {documentId && !user.email?.trim() && (
        <Text fontSize="xs" color="fg.muted">
          Set your email in Settings to use document messages.
        </Text>
      )}
      {messagesFeatureActive ? (
        <>
          {!focusedRootId && messagesError ? (
            <Text fontSize="xs" color="fg.error">
              Could not load document messages.
            </Text>
          ) : null}
          {focusedRootId ? (
            <>
              <Box
                minH="100px"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                width="100%"
              >
                <MessageList
                  messages={displayMessages}
                  loading={isMessagesInitialLoad}
                  error={messagesError ?? undefined}
                  emptyMessage="No messages for this document yet."
                  focusedRootId={focusedRootId}
                  onExpandThread={expandThread}
                  messagesEndRef={messagesEndRef}
                  onRunChatMessageQuery={handleRunChatMessageQuery}
                  chatMessageQueryLoadingId={chatMessageQueryLoadingId}
                  alignMessagesToBottom
                  onMessageDeleted={() => void refetchMessages()}
                />
              </Box>
              <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
                <Text>Replying in thread</Text>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => collapseThread()}
                >
                  Cancel
                </Button>
              </HStack>
            </>
          ) : null}
        </>
      ) : null}
      <ChatComposerRow
        rootRef={composerRootRef}
        value={promptText}
        onChange={setPromptText}
        onSubmit={onSubmitAiEdit}
        placeholder={placeholder}
        disabled={aiSubmitDisabled}
        loading={processingPrompt}
        trailingActions={threadTrailing}
        modelPicker={
          <DocumentComposerModelPicker
            value={sessionComposerModel}
            onValueChange={setSessionComposerModel}
            options={composerModelOptions}
            disabled={aiSubmitDisabled}
          />
        }
      />
    </VStack>
  );

  if (!active) {
    return null;
  }

  return <PageContentSidebarPortal>{footerContent}</PageContentSidebarPortal>;
}
